import ShoppingCartIcon from '@/components/icons/shopping-cart-icon';

import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useModal from '@/hooks/useModal';
import { retrieveCart } from '@/lib/actions/cart/action';
import { cn, formatIDR } from '@/lib/utils';
import { ThemeType } from '@/stores/builder';
import { HttpTypes } from '@/types';
import { BaseCart } from '@/types/http/cart/common';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import VoucherList from '@/components/sections/builder/elements/header/header/cart-dropdown/voucher-list';
import Cookies from 'js-cookie';
import CartList from '@/components/sections/builder/elements/header/header/cart-dropdown/cart-list';
import { useGetListPromotions, useMutateResetPromotion } from '@/hooks/api/storefront/promotion';
import { BasePromotion } from '@/types/http/promotion/common';
import FreeGiftList from '@/components/sections/builder/elements/header/header/cart-dropdown/free-gift-list';
import { usePromotionStore } from '@/stores/promotion';
import { shallow } from 'zustand/shallow';
import ModalAlert from '@/components/sections/modal-alert';
import { toast } from '@/components/ui/use-toast';
import ActiveOffersList from '@/components/sections/builder/elements/header/header/cart-dropdown/active-offers-list';
import { dummyCarts } from '@/constant/dummyData/dummy-carts';
import { queryClient } from '@/lib/query-client';

export interface CartDrawerProps {
  openModal?: boolean;
  cart: HttpTypes.StoreCart;
  setOpenModal?: (open: boolean) => void;
  host?: string;
  theme: ThemeType;
  preview?: boolean;
}

export function CartDrawer({
  openModal,
  setOpenModal,
  cart,
  host,
  theme,
  preview,
}: CartDrawerProps) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = preview ? query.get('token') : Cookies.get('accessToken') || '';
  const modalOverlap = useModal();

  const [currentItemQty, setCurrentItemQty] = useState<{ [key: string]: number }>({});
  const [drawerView, setDrawerView] = useState<'cart' | 'voucher' | 'gift' | 'active_offers'>(
    'cart'
  );
  const [promoReward, setPromoReward] = useState<BasePromotion | null>(null);
  const [maxQtyReward, setMaxQtyReward] = useState<number>(0);
  const [voucherCode, setVoucherCode] = useState<string>('');
  const [errorVoucher, setErrorVoucher] = useState<string | null>(null);
  const [pendingVoucher, setPendingVoucher] = useState<{
    promoRewards: BasePromotion[];
    code: string;
  } | null>(null);

  const cartItems = preview ? dummyCarts.items : cart?.items;
  const isEmptyCart = cartItems?.length === 0;

  const promotionStore = usePromotionStore(
    (state) => ({
      selectedPromotion: state.selectedPromotion,
      selectedFreeGift: state.selectedFreeGift,
      addPromotion: state.addPromotion,
      applyVoucher: state.applyVoucher,
      syncFreeGiftPromotion: state.syncFreeGiftPromotion,
      replaceFreeGift: state.replaceFreeGift,
      applyPromotion: state.applyPromotion,
      removePromotion: state.removePromotion,
      applyVoucherRewards: state.applyVoucherRewards,
    }),
    shallow
  );

  const subtotal = useMemo(() => {
    return cartItems?.reduce((total, item) => {
      const price = Number(
        Number(item.discount_price) > 0 && Number(item.discount_price) < Number(item.sell_price)
          ? item.discount_price
          : item.sell_price
      );
      return total + price * item.quantity;
    }, 0);
  }, [cartItems]);

  const payloadListPromotion = useMemo(() => {
    const items = (cart?.items as BaseCart[]) || [];

    const { mappedItems, grandTotal } = items.reduce(
      (acc, item) => {
        const discountPrice = item.discount_price ?? 0;
        const discountAmount = item.discount_amount ?? 0;
        const sellPrice = item.sell_price;

        const pricePerItem = discountPrice > 0 ? discountPrice : sellPrice;
        const itemTotal = item.quantity * pricePerItem;

        acc.mappedItems.push({
          item_id: item.id,
          qty: item.quantity,
          amount: itemTotal,
        });

        acc.grandTotal += itemTotal - discountAmount;

        return acc;
      },
      {
        mappedItems: [] as {
          item_id: string;
          qty: number;
          amount: number;
        }[],
        grandTotal: 0,
      }
    );

    return {
      items: mappedItems,
      grand_total: grandTotal,
      current_date: new Date().toISOString(),
    };
  }, [cart?.items]);

  const { data: recommendPromotion, isLoading: isLoadingRecommend } = useGetListPromotions(
    payloadListPromotion,
    true,
    token as string,
    {
      enabled: !!payloadListPromotion.items?.length && openModal,
    }
  );

  const { data: listPromotion, isLoading: isLoadingPromotions } = useGetListPromotions(
    payloadListPromotion,
    false,
    token as string,
    {
      enabled: !!payloadListPromotion.items.length && openModal,
    }
  );

  const allPromotions = useMemo(() => {
    const selectedIds = new Set(
      promotionStore.selectedPromotion.map((promo) => promo.promotion_id)
    );
    const merged = [...promotionStore.selectedPromotion];

    listPromotion?.forEach((promo) => {
      if (!selectedIds.has(promo.promotion_id)) {
        merged.push(promo);
      }
    });

    return merged;
  }, [listPromotion, promotionStore.selectedPromotion]);

  const handleApplyVoucher = (voucherIds: string[]) => {
    if (!allPromotions) return;

    promotionStore.applyVoucher(voucherIds, allPromotions);
  };

  const handleSubmitRedeemVoucher = (promoRewards: BasePromotion[], code: string) => {
    const isNewOverlapFalse = promoRewards.some((promo) => promo.is_overlap === false);
    const selectedPromos = promotionStore.selectedPromotion || [];

    const hasExistingOverlapFalse = selectedPromos.some((promo) => promo.is_overlap === false);
    const hasExistingOverlapTrue = selectedPromos.every((promo) => promo.is_overlap === true);

    const hasConflict =
      (hasExistingOverlapTrue && isNewOverlapFalse) ||
      (hasExistingOverlapFalse && !isNewOverlapFalse);

    if (hasConflict) {
      setPendingVoucher({ promoRewards, code });
      modalOverlap.setIsShow(true);
      return;
    }

    promotionStore.applyVoucherRewards(promoRewards, code, isNewOverlapFalse);
  };

  const handleConfirmOverlap = () => {
    if (pendingVoucher) {
      const isNewOverlapFalse = pendingVoucher.promoRewards.some((p) => p.is_overlap === false);
      promotionStore.applyVoucherRewards(
        pendingVoucher.promoRewards,
        pendingVoucher.code,
        isNewOverlapFalse
      );
    }
    modalOverlap.setIsShow(false);
    setPendingVoucher(null);
    setDrawerView('cart');
    toast({
      title: 'Success',
      variant: 'success',
      description: 'Promotion has been applied',
      duration: 3000,
    });
  };

  const handleRemovePromotion = (promoId: string) => {
    promotionStore.removePromotion(promoId);
  };

  useEffect(() => {
    if (cartItems?.length) {
      setCurrentItemQty(Object.fromEntries(cartItems.map((i) => [i.id, i.quantity])));
    } else {
      setCurrentItemQty({});
    }
  }, [cartItems]);

  useEffect(() => {
    if (!listPromotion || !listPromotion.length || preview) return;

    const recommendedPromo = recommendPromotion?.find(
      (promo) => payloadListPromotion.grand_total >= promo.minimum_spending
    );
    const fallbackPromo = listPromotion
      ?.filter((promo) => payloadListPromotion.grand_total >= promo.minimum_spending)
      .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];

    const promoToApply = recommendedPromo || fallbackPromo;
    if (!promoToApply) return;

    const selectedPromos = promotionStore.selectedPromotion || [];
    const allSelectedOverlapTrue = selectedPromos.every((promo) => promo.is_overlap === true);
    const newPromoOverlapTrue = promoToApply.is_overlap === true;

    if (allSelectedOverlapTrue && newPromoOverlapTrue) {
      promotionStore.addPromotion(promoToApply, payloadListPromotion.grand_total, listPromotion);
    } else {
      promotionStore.applyPromotion(promoToApply);
    }
  }, [listPromotion, recommendPromotion, payloadListPromotion.grand_total, cart?.promotions]);

  useEffect(() => {
    promotionStore.syncFreeGiftPromotion();
  }, [promotionStore.selectedPromotion]);

  return (
    <>
      <Drawer
        open={openModal}
        onOpenChange={(isOpen) => {
          setOpenModal?.(isOpen);
          if (!isOpen) {
            setDrawerView('cart');
          }
        }}
        direction='right'
      >
        <DrawerContent className='theme_cart_drawer_bg theme_font theme_drawer fixed inset-y-0 right-0 z-[106] flex h-auto w-full max-w-[420px] flex-col border'>
          {drawerView === 'voucher' ? (
            <VoucherList
              cart={cart}
              promotionTheme={theme?.promotion}
              drawerView={drawerView}
              setDrawerView={setDrawerView}
              openModal={openModal as boolean}
              token={token as string}
              listPromotions={allPromotions}
              isLoadingListPromotions={isLoadingPromotions}
              payloadListPromotion={payloadListPromotion}
              voucherCode={voucherCode}
              setVoucherCode={setVoucherCode}
              setErrorVoucher={setErrorVoucher}
              errorVoucher={errorVoucher}
              onApplyVoucher={handleApplyVoucher}
              onRedeemVoucher={handleSubmitRedeemVoucher}
            />
          ) : drawerView === 'cart' ? (
            <>
              {isEmptyCart ? (
                <>
                  <DrawerHeader className='flex items-center justify-between text-left'>
                    <DrawerTitle />
                    <DrawerClose>
                      <XIcon className='theme_cart_drawer_color h-4 w-4' />
                    </DrawerClose>
                  </DrawerHeader>
                  <div className='flex h-full items-center justify-center px-4'>
                    <div className='theme_cart_drawer_color text-xl'>Your cart is empty</div>
                  </div>
                </>
              ) : (
                <CartList
                  cart={cart}
                  cartItems={cartItems as BaseCart[]}
                  setDrawerView={setDrawerView}
                  setOpenModal={(value) =>
                    setOpenModal?.(typeof value === 'boolean' ? value : value(false))
                  }
                  token={token as string}
                  subtotal={subtotal}
                  setCurrentItemQty={setCurrentItemQty}
                  currentItemQty={currentItemQty}
                  host={host as string}
                  themeId={themeId as string}
                  theme={theme}
                  isPreview={preview}
                  recommendPromotion={recommendPromotion}
                  isLoadingRecommend={isLoadingRecommend}
                  payloadListPromotion={payloadListPromotion}
                  setPromoReward={setPromoReward}
                  setMaxQtyReward={setMaxQtyReward}
                  colorScheme={theme?.promotion?.colorScheme}
                />
              )}
            </>
          ) : drawerView === 'gift' ? (
            <FreeGiftList
              drawerView={drawerView}
              token={token as string}
              promotion={promoReward as BasePromotion}
              setDrawerView={setDrawerView}
              maxQty={maxQtyReward}
              onSaveGift={(data) => {
                promotionStore.replaceFreeGift(String(promoReward?.promotion_id), data);
              }}
              colorScheme={theme?.promotion?.colorScheme}
              initialSelectedGifts={promotionStore.selectedFreeGift.filter(
                (gift) => gift.promotion_id === promoReward?.promotion_id
              )}
            />
          ) : (
            <ActiveOffersList
              colorSchemeCart={theme?.cart?.drawerColorScheme}
              promotionTheme={theme?.promotion}
              setDrawerView={setDrawerView}
              listPromotions={promotionStore.selectedPromotion}
              grandTotal={payloadListPromotion.grand_total}
              onRemovePromotion={(promo) => handleRemovePromotion(promo.promotion_id)}
            />
          )}
        </DrawerContent>
      </Drawer>

      <ModalAlert
        openModal={modalOverlap.isShow}
        setOpenModal={modalOverlap.setIsShow}
        title='Are you sure'
        description='These vouchers cannot overlap with existing promotions, do you want to replace them?'
        onConfirm={handleConfirmOverlap}
      />
    </>
  );
}

export function CartPopup({
  openModal,
  setOpenModal,
  cart,
  host,
}: {
  openModal?: boolean;
  cart: BaseCart | null;
  setOpenModal?: (open: boolean) => void;
  host: string;
}) {
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const token = query.get('token') || '';

  const { data: carts } = useQuery({
    queryKey: ['carts'],
    queryFn: async () => {
      const carts = await retrieveCart(host as string);
      return carts?.data;
    },
  });
  const [currentItemQty] = React.useState<{ [key: string]: number }>({});
  const [subtotal, setSubtotal] = React.useState<number>(0);

  const cartItems = carts?.items;

  React.useEffect(() => {
    const newSubtotal = cartItems?.reduce((total, item) => {
      const qty = currentItemQty[item.id] || item.quantity;
      return total + (item.discount_price ? item.discount_price : item.sell_price) * qty;
    }, 0);
    setSubtotal(newSubtotal || 0);
  }, [currentItemQty, cartItems]);

  const images = cart?.product_assets[0];
  const displayedQuantity = cart?.quantity || 0;
  const totalPrice = displayedQuantity * (cart?.sell_price || 0);

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='top'>
      <DrawerContent className='theme_cart_drawer_bg theme_font fixed inset-x-0 top-0 z-50 flex h-auto w-full flex-col transition-all duration-1000'>
        {cart ? (
          <div className='theme_cart_drawer_color mx-2 px-4 pb-6 pt-2'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-inherit'>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Baru Saja Ditambahkan
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Barang
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Jumlah
                  </TableHead>
                  <TableHead className='[&:not(text-muted-foreground)]:text-inherit'>
                    Total Harga
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className='hover:bg-inherit'>
                  <TableCell>
                    <div className='w-44'>
                      <Image
                        src={typeof images === 'object' ? images?.assets_url : images || ''}
                        width={100}
                        height={100}
                        className='h-auto w-full object-cover'
                        alt={cart.product_name}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{cart.product_name}</TableCell>
                  <TableCell>{cart.quantity}</TableCell>
                  <TableCell>{formatIDR(totalPrice || 0)}</TableCell>
                  <TableCell>
                    <div className='flex flex-col items-end text-end'>
                      <p>{formatIDR(subtotal)}</p>
                      <span className='text-xs font-semibold italic'>
                        *Belum Termasuk Biaya Pengiriman{' '}
                      </span>
                      <div className='flex gap-1 '>
                        <DrawerClose>
                          <Button type='button'>Continue Shopping</Button>
                        </DrawerClose>
                        <Button type='button' variant='outline'>
                          <Link href={`/${token ? `?theme_id=${themeId}&token=${token}` : ''}cart`}>
                            Cart
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

export function CartDropdownButton({
  preview,
  cart,
  host,
  theme,
  content,
}: {
  preview?: boolean;
  cart: HttpTypes.StoreCart;
  host: string;
  theme?: ThemeType;
  content?: HeaderElementDataType;
}) {
  const cartDrawer = useModal();
  const cartItems = cart?.items;
  const query = useSearchParams();
  const themeId = query.get('theme_id') || '';
  const cartIdCookie = Cookies.get('_jstore_cart_id');
  const token = Cookies.get('accessToken');

  const { clearSelectedPromotions } = usePromotionStore(
    (state) => ({
      clearSelectedPromotions: state.clearSelectedPromotions,
    }),
    shallow
  );

  const resetPromotion = useMutateResetPromotion({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      clearSelectedPromotions();
    },
  });

  const totalItems =
    cartItems?.reduce((acc, item) => {
      return acc + item.quantity;
    }, 0) || 0;

  const backgroundColor = content?.colorSchemeHeader?.background || '#ffffff';
  const isDark = React.useMemo(() => isDarkColor(backgroundColor), [backgroundColor]);

  useEffect(() => {
    const cartId = cartIdCookie || cart?.cartId;
    if (cartId && (cart?.promotions?.length ?? 0) > 0 && cartDrawer.isShow) {
      resetPromotion.mutate({ cartId, token });
    }
  }, [cartIdCookie, cart?.cartId, cart?.promotions?.length, cartDrawer.isShow, token]);

  return (
    theme && (
      <>
        <div className='theme_cart_page_button'>
          <Link href={`/cart${preview ? `?isPreview=true&theme_id=${themeId as string}` : ''}`}>
            <div className='relative'>
              <ShoppingCartIcon className='h-7 w-7' data-test='cart-icon' />
              {cartItems && cartItems?.length > 0 && (
                <div
                  className={cn(
                    'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                    isDark ? 'bg-white text-black' : 'bg-black text-white'
                  )}
                >
                  {totalItems > 99 ? '99+' : totalItems}
                </div>
              )}
            </div>
          </Link>
        </div>
        <button onClick={() => cartDrawer.open()} className='theme_cart_drawer_button'>
          <div className='relative'>
            <ShoppingCartIcon className='h-7 w-7' />
            {cartItems && cartItems.length > 0 && (
              <div
                className={cn(
                  'absolute -bottom-2 flex h-[18px] w-[18px] items-center justify-center rounded-full p-0.5 text-[9px]',
                  isDark ? 'bg-white text-black' : 'bg-black text-white'
                )}
              >
                {totalItems > 99 ? '99+' : totalItems}
              </div>
            )}
          </div>
        </button>
        <CartDrawer
          openModal={cartDrawer.isShow}
          setOpenModal={cartDrawer.setIsShow}
          cart={cart}
          host={host}
          theme={theme}
          preview={preview}
        />
      </>
    )
  );
}

function isDarkColor(hex: string): boolean {
  if (!hex) return false;

  const rgb = hex.match(/\w\w/g)?.map((x) => parseInt(x, 16));
  if (!rgb) return false;

  const [r, g, b] = rgb;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance < 128;
}
